/**
 * "My IT activity" — what is already in flight, on the conversation's home.
 *
 * This panel replaced "Recent questions" in the rail, and the swap is the whole
 * point of the redesign rather than a cosmetic one. A conversation can answer a
 * question, troubleshoot a fault and raise a ticket; the single thing it cannot
 * do is tell someone, at a glance and without being asked, that IT replied to
 * them yesterday and is waiting. That is what an employee actually arrives
 * wanting to know, so it is what the rail shows.
 *
 * Everything here is live Ivanti data through `useTicketList` — the same
 * endpoint `/tickets` renders, with the same server-side entitlement trimming.
 * Nothing on this panel is mocked. Two buckets are read rather than one:
 * `OPEN` is partitioned locally on the `awaiting_user` flag the API already
 * computes, and `RESOLVED` supplies the short "did that fix it?" tail, because
 * a resolved ticket nobody confirms is the most common way a service desk
 * loses track of whether it actually helped.
 *
 * Ordering is deliberate: anything waiting on the employee sits at the top,
 * work in progress under it, recently resolved last. The waiting group is
 * marked with the accent border rather than a danger tone — it needs to catch
 * the eye without implying something has gone wrong, because usually nothing
 * has.
 *
 * 6 Oct (product owner): the access cases the reader is involved in sit
 * here too, one card each ("Waiting on Me" when they have a step, approvals
 * included), and their requests are left out of the ticket groups.
 *
 * Not shown, deliberately: approvals *assigned to* this person. There is no
 * endpoint for "approvals awaiting my decision" — the ticket list returns the
 * tickets someone raised, not the ones they must approve — and inventing one
 * behind mock data would put a number on this panel that no system stands
 * behind.
 */

import { ChevronRight, Icon, Inbox } from "@norican/icons";
import { Badge, Card, cn, Separator, Typography } from "@norican/react";
import { Link } from "react-router";

import {
  ErrorState,
  isRequesterUnresolvedError,
  isRequesterUnresolvedResult,
  LoadingRows,
  RelativeTime,
  RequesterUnresolvedNotice,
} from "@/components/common";
import { useMyAccessCases } from "@/features/access/api";
import { useT } from "@/i18n";
import { type AccessCaseSummary, useKiosk, useMe } from "@/lib/api";

import type { TicketSummary } from "./api/contract";
import { useTicketList } from "./api/queries";
import { AccessCaseCardRow } from "./components/access-case-row";
import { TicketStatusBadge } from "./components/ticket-badges";
import { TranslatableTextView } from "./components/translatable-text";
import { groupedTicketIds, splitAccessCases, withoutGrouped } from "./lib/access-rows";
import { ticketReferenceKey } from "./lib/ticket-display";

/** Enough to be useful in an 18rem rail without turning it into a second list page. */
const OPEN_LIMIT = 8;
const RESOLVED_LIMIT = 3;

interface ActivityRowProps {
  ticket: TicketSummary;
  /** Draws the accent edge and surfaces the "waiting for you" line. */
  needsYou?: boolean;
}

function ActivityRow({ ticket, needsYou = false }: ActivityRowProps) {
  const { t } = useT("tickets");
  const unread = ticket.unread_updates ?? 0;

  return (
    <Card
      padding="sm"
      className={cn(
        "ease-standard transition hover:shadow-md",
        // The attention marker is a left edge in the accent, not a red card.
        // Something waiting on you is normal service, not an incident.
        needsYou && "border-l-accent border-l-4",
      )}
    >
      <Link to={ticket.url_path} className="flex flex-col gap-2 rounded-md">
        <div className="flex items-center justify-between gap-2">
          <Typography
            as="span"
            variant="caption"
            /* `accent-fg` is WHITE in the light theme — it is the colour for
               text sitting *on* an accent fill. On a white card it rendered
               invisible, which is why every ticket number was missing.
               `accent-fg-subtle` is the paired token for accent-coloured text
               on a light surface. */
            className="text-accent-fg-subtle font-mono font-semibold tracking-tight"
          >
            {t(ticketReferenceKey(ticket.kind, "id"), { number: ticket.ticket_number })}
          </Typography>
          <Icon icon={ChevronRight} size="sm" className="text-fg-subtle shrink-0" />
        </div>

        <Typography as="span" variant="body-sm" className="text-fg line-clamp-2 font-medium">
          <TranslatableTextView value={ticket.subject} hideBadge />
        </Typography>

        <div className="flex flex-wrap items-center gap-2">
          <TicketStatusBadge status={ticket.status} kind={ticket.kind} />
          {unread > 0 ? (
            <Badge tone="accent" variant="solid" size="xs">
              {t("list.unread", { count: unread })}
            </Badge>
          ) : null}
          <Typography as="span" variant="caption" tone="muted">
            <RelativeTime value={ticket.updated_at} />
          </Typography>
        </div>

        {needsYou ? (
          <Typography as="span" variant="caption" className="text-accent-fg-subtle font-medium">
            {t("list.awaitingYou")}
          </Typography>
        ) : null}
      </Link>
    </Card>
  );
}

export interface ActivityGroupProps {
  label: string;
  tickets: readonly TicketSummary[];
  /** Access cases, listed before the tickets (6 Oct). */
  accessCases?: readonly AccessCaseSummary[];
  needsYou?: boolean;
  /**
   * Suppress the group's own heading.
   *
   * Inside a `SupportSection` the heading is already on the section's trigger,
   * and printing it again put "Waiting on Me" on screen twice, stacked. The
   * label is still used for the list's accessible name.
   */
  hideLabel?: boolean;
}

export function ActivityGroup({
  label,
  tickets,
  accessCases = [],
  needsYou = false,
  hideLabel = false,
}: ActivityGroupProps) {
  if (tickets.length === 0 && accessCases.length === 0) {
    return null;
  }
  return (
    <section className="flex flex-col gap-2" aria-label={label}>
      {hideLabel ? null : (
        <Typography as="h3" variant="caption" tone="muted" className="tracking-wide uppercase">
          {label}
        </Typography>
      )}
      <ul className="flex list-none flex-col gap-2 p-0">
        {accessCases.map((item) => (
          <li key={`access:${item.case_id}`}>
            <AccessCaseCardRow item={item} needsYou={needsYou} />
          </li>
        ))}
        {tickets.map((ticket) => (
          <li key={ticket.ticket_id}>
            <ActivityRow ticket={ticket} needsYou={needsYou} />
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface MyItActivityProps {
  className?: string;
}

/**
 * The panel's data, lifted so a page can choose its layout before rendering.
 *
 * `AskPage` needs to know whether there is anything to show *before* it picks a
 * grid template: an empty panel holding an 18rem column open is 18rem the
 * conversation could have had. Both callers share one query cache, so asking
 * twice costs one request, not two.
 */
export function useMyItActivity() {
  const open = useTicketList({
    bucket: "OPEN",
    limit: OPEN_LIMIT,
    sort: "UPDATED_AT",
    order: "DESC",
  });
  const resolved = useTicketList({
    bucket: "RESOLVED",
    limit: RESOLVED_LIMIT,
    sort: "UPDATED_AT",
    order: "DESC",
  });

  // 6 Oct: the access cases the reader is involved in; their requests are
  // grouped under each case's card, not listed among the tickets.
  const { data: me } = useMe();
  const access = useMyAccessCases(me?.access_front_door === true);
  const grouped = groupedTicketIds(access.data?.items);
  const cases = splitAccessCases(access.data?.items);
  const openItems = withoutGrouped(open.data?.items ?? [], grouped);
  const waiting = openItems.filter((ticket) => ticket.awaiting_user === true);
  const inProgress = openItems.filter((ticket) => ticket.awaiting_user !== true);
  const resolvedItems = withoutGrouped(resolved.data?.items ?? [], grouped);
  /* Ivanti has no record of this person. Worth the column on its own: it is
     the one place on the Ask page that can say why nothing is listed and who
     to contact, before they try to raise something and hit the same wall. */
  const requesterUnresolved =
    isRequesterUnresolvedResult(open.data?.result) ||
    isRequesterUnresolvedResult(resolved.data?.result) ||
    isRequesterUnresolvedError(open.error);

  return {
    open,
    waiting,
    inProgress,
    resolvedItems,
    accessWaiting: cases.waiting,
    accessInProgress: cases.inProgress,
    accessFinished: cases.finished.slice(0, RESOLVED_LIMIT),
    requesterUnresolved,
    /* Pending counts as empty so the column does not flash open and shut on
       every load; it opens once there is genuinely something in it. */
    isEmpty:
      !requesterUnresolved &&
      (open.isPending ||
        (waiting.length === 0 &&
          inProgress.length === 0 &&
          resolvedItems.length === 0 &&
          (access.data?.items ?? []).length === 0)),
  };
}

export function MyItActivity({ className }: MyItActivityProps) {
  const { t } = useT("tickets");
  const {
    open,
    waiting,
    inProgress,
    resolvedItems,
    accessWaiting,
    accessInProgress,
    accessFinished,
    requesterUnresolved,
    isEmpty,
  } = useMyItActivity();
  // "My" activity on a shared device would be the device's (1 Oct).
  const kiosk = useKiosk();
  if (kiosk !== null) {
    return null;
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="flex min-w-0 items-center gap-2">
        <Icon icon={Inbox} size="sm" tone="accent" />
        <Typography as="h2" variant="heading-6" className="min-w-0 flex-1 text-balance">
          {t("activity.title")}
        </Typography>
        {isEmpty ? null : (
          <Link
            to="/tickets"
            className="text-accent-fg-subtle shrink-0 text-sm font-medium underline-offset-2 hover:underline"
          >
            {t("activity.viewAll")}
          </Link>
        )}
      </div>

      <Separator />

      {requesterUnresolved && !open.isError ? <RequesterUnresolvedNotice /> : null}

      {open.isPending ? (
        <LoadingRows rows={3} />
      ) : open.isError ? (
        <ErrorState variant="inline" error={open.error} onRetry={() => void open.refetch()} />
      ) : requesterUnresolved &&
        waiting.length === 0 &&
        inProgress.length === 0 &&
        resolvedItems.length === 0 &&
        accessWaiting.length === 0 &&
        accessInProgress.length === 0 ? null : isEmpty ? (
        /* Deliberately not an `EmptyStateBlock`: that reserves an icon, a
           heading and a paragraph of vertical space to say "nothing here",
           which is the one message that does not deserve it. Two lines and a
           quiet link, and the conversation keeps the page. */
        <div className="flex flex-col gap-1">
          <Typography as="p" variant="body-sm" className="text-fg font-medium">
            {t("activity.allCaught.title")}
          </Typography>
          <Typography as="p" variant="caption" tone="muted" className="text-pretty">
            {t("activity.allCaught.description")}
          </Typography>
          <Link
            to="/tickets"
            className="text-accent-fg-subtle mt-1 text-sm font-medium underline-offset-2 hover:underline"
          >
            {t("activity.recent")}
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <ActivityGroup
            label={t("activity.needsYou")}
            tickets={waiting}
            accessCases={accessWaiting}
            needsYou
          />
          <ActivityGroup
            label={t("activity.inProgress")}
            tickets={inProgress}
            accessCases={accessInProgress}
          />
          <ActivityGroup
            label={t("activity.resolved")}
            tickets={resolvedItems}
            accessCases={accessFinished}
          />
        </div>
      )}
    </div>
  );
}
